import { styled } from "@mui/material";

import { TEST_IDS } from "constants/TestIds";
import { useActive } from "hooks/useActive";
import { shouldForwardPropHelper } from "utils/shouldForwardPropHelper";

import type { ReactNode } from "react";

export interface TreeGroupProps {
  children: ReactNode;
  label: ReactNode;
  "data-testid"?: string;
}

export function TreeGroup({
  children,
  label,
  "data-testid": dataTestId,
}: TreeGroupProps) {
  const { isActive, toggleActive } = useActive();

  return (
    <details
      open={isActive}
      data-testid={dataTestId}
      onClick={(event) => event.preventDefault()}
    >
      <HeaderWrapper isExpanded={isActive} onClick={toggleActive}>
        {label}
      </HeaderWrapper>
      <TreeItemsWrapper
        data-testid={dataTestId && TEST_IDS.getItems(dataTestId)}
      >
        {children}
      </TreeItemsWrapper>
    </details>
  );
}

interface HeaderWrapperProps {
  isExpanded: boolean;
}

const WRAPPER_PROPS = new Set(["isExpanded"]);

const HeaderWrapper = styled("summary", {
  shouldForwardProp: (prop) => shouldForwardPropHelper(prop, WRAPPER_PROPS),
})<HeaderWrapperProps>(({ isExpanded, theme }) => ({
  display: "flex",
  padding: theme.spacing(1.5, 1.5, 1.5, 0),
  borderRadius: 4,
  overflowX: "auto",
  cursor: "pointer",
  transition: "box-shadow .3s ease-out, background-color .3s ease-out",
  alignItems: "center",

  "&::-webkit-details-marker": {
    display: "none",
  },

  "&:focus": {
    outline: "none",
  },
}));

const TreeItemsWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginLeft: theme.spacing(2.5),
}));
